/**
 * <RunLifecycle> — 13-ui-screens.md §12.2.
 *
 * Four numbered pill steps with 22 × 1 px connectors. antd `<Steps>` renamed
 * `direction` → `orientation` in v6 and still cannot produce pill steps, so
 * this is bespoke.
 *
 * The Locked stage uses the deep-green filled treatment, matching the Locked
 * pill — the only filled-dark surface in the system.
 */
import type { CSSProperties } from 'react';

export const RUN_STAGES = ['Open', 'Closed', 'Reconcile', 'Locked'] as const;
export type RunStage = (typeof RUN_STAGES)[number];

export interface RunLifecycleProps {
  stages?: readonly string[];
  /** 0-based index of the current stage. */
  current: number;
}

function pillStyle(state: 'current' | 'done' | 'future', isLocked: boolean): CSSProperties {
  const base: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    padding: '5px 11px',
    borderRadius: 4,
    fontWeight: 600,
    fontSize: 12,
    borderWidth: 1,
    borderStyle: 'solid',
  };
  if (state === 'current' && isLocked) {
    return { ...base, borderColor: 'var(--brand-primary)', background: 'var(--brand-deep)', color: 'var(--white)' };
  }
  if (state === 'current') {
    return { ...base, borderColor: 'var(--brand-primary)', background: 'var(--green-100)', color: 'var(--green-800)' };
  }
  if (state === 'done') {
    return { ...base, borderColor: 'var(--border-subtle)', background: 'var(--white)', color: 'var(--neutral-600)' };
  }
  return { ...base, borderColor: 'var(--border-subtle)', background: 'var(--white)', color: 'var(--neutral-400)' };
}

function dotStyle(state: 'current' | 'done' | 'future', isLocked: boolean): CSSProperties {
  const base: CSSProperties = {
    width: 17,
    height: 17,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'var(--font-mono)',
    fontSize: 10,
    fontWeight: 600,
  };
  if (state === 'current' && isLocked) return { ...base, background: 'var(--brand-accent)', color: 'var(--brand-deep)' };
  if (state === 'current') return { ...base, background: 'var(--brand-primary)', color: 'var(--white)' };
  if (state === 'done') return { ...base, background: 'var(--green-200)', color: 'var(--neutral-600)' };
  return { ...base, background: 'var(--neutral-100)', color: 'var(--neutral-600)' };
}

export function RunLifecycle({ stages = RUN_STAGES, current }: RunLifecycleProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', padding: '14px 0 0' }}>
      {stages.map((stage, i) => {
        const state = i === current ? 'current' : i < current ? 'done' : 'future';
        const isLocked = state === 'current' && i === stages.length - 1;
        return (
          <div key={stage} style={{ display: 'flex', alignItems: 'center' }}>
            <div style={pillStyle(state, isLocked)}>
              <span style={dotStyle(state, isLocked)}>{i + 1}</span>
              <span>{stage}</span>
            </div>
            {i < stages.length - 1 ? (
              <span
                style={{
                  display: 'inline-block',
                  width: 22,
                  height: 1,
                  margin: '0 4px',
                  background: i < current ? 'var(--brand-accent)' : 'var(--border-subtle)',
                }}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
